import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TaskFilters {
  assignedTo?: string | null;
  priority?: string | null;
  taskType?: 'cs_escalation' | 'kommo_review' | 'regular' | null;
  status?: string | null;
  search?: string | null;
}

export interface FilteredTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  assigned_to?: string | null;
  due_date?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown> | null;
  notes?: string | null;
  estimated_hours?: number | null;
  case_id?: string | null;
  ticket_id?: string | null;
  contact_id?: string | null;
  deal_id?: string | null;
  assigned_user_name?: string | null;
  assigned_user_first_name?: string | null;
  assigned_user_last_name?: string | null;
  created_by?: string | null;
  created_by_user_name?: string | null;
  created_by_first_name?: string | null;
  created_by_last_name?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_phone?: string | null;
  deal_name?: string | null;
}

export interface TaskCounts {
  pending: number;
  in_progress: number;
  completed: number;
  blocked: number;
}

interface RpcResponse {
  tasks: FilteredTask[];
  counts: TaskCounts;
}

export function useFilteredTasks(filters: TaskFilters = {}) {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'tasks-filtered',
      filters.assignedTo,
      filters.priority,
      filters.taskType,
      filters.status,
      filters.search,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_filtered_tasks', {
        p_assigned_to: filters.assignedTo || null,
        p_priority: filters.priority || null,
        p_task_type: filters.taskType || null,
        p_status: filters.status || null,
        p_search: filters.search || null,
      });

      if (error) throw error;
      return data as unknown as RpcResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: unknown }) => {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-notifications-topbar'] });
      toast.success('Tarefa atualizada!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar tarefa: ' + error.message);
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-notifications-topbar'] });
      toast.success('Tarefa excluída!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao excluir tarefa: ' + error.message);
    },
  });

  const createTask = useMutation({
    mutationFn: async (newTask: { title: string; [key: string]: unknown }) => {
      const { data, error } = await supabase
        .from('tasks')
        .insert([newTask])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa criada!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar tarefa: ' + error.message);
    },
  });

  return {
    tasks: data?.tasks || [],
    counts: data?.counts || { pending: 0, in_progress: 0, completed: 0, blocked: 0 },
    isLoading,
    error,
    refetch,
    updateTask,
    deleteTask,
    createTask,
    invalidate: () => queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] }),
  };
}
